import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './VIImage.css';
import deleteIcon from '../../../assets/icons/delete_all.svg';
import muteIcon from '../../../assets/icons/icon_audio_off.svg';
import unmuteIcon from '../../../assets/icons/icon_audio_on.svg';

interface VIImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  className?: string;
  style?: React.CSSProperties;
  onClick?: (e: React.MouseEvent<HTMLImageElement>) => void;
}

const VIImage: React.FC<VIImageProps> = ({ src, alt, className, style, onClick }) => {
  const [showModal, setShowModal] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);
  const isVideo = src.endsWith('.mp4') || src.endsWith('.mov') || src.includes('video');

  const handleMuteToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(videoRef.current.muted);
    }
  };

  useEffect(() => {
    if (!isVideo || !videoRef.current) return;

    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.7,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && videoRef.current) {
          videoRef.current.play().catch(err => {
            console.log('Autoplay failed:', err);
          });
        } else if (videoRef.current) {
          videoRef.current.pause();
        }
      });
    }, options);

    observer.observe(videoRef.current);

    return () => {
      observer.disconnect();
    };
  }, [isVideo]);

  const handleClick = (e: React.MouseEvent) => {
    console.log('🖼️ VIImage clicked:', {
      src,
      hasCustomOnClick: !!onClick,
      event: e.type,
      target: e.target
    });
    
    e.stopPropagation();
    
    if (onClick) {
      console.log('📲 Calling custom onClick handler');
      onClick(e as React.MouseEvent<HTMLImageElement>);
    } else {
      console.log('📲 Using default modal behavior');
      setShowModal(true);
    }
  };

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showModal]);

  const renderMedia = () => {
    if (isVideo) {
      return (
        <div className="vi-video-container" style={style}>
          <video
            ref={videoRef}
            src={src}
            className={className}
            style={style}
            onClick={handleClick}
            playsInline
            muted={isMuted}
            loop
          />
          <button 
            className="vi-video-mute-button"
            onClick={handleMuteToggle}
            aria-label={isMuted ? "Unmute" : "Mute"}
          >
            <img 
              src={isMuted ? muteIcon : unmuteIcon} 
              alt={isMuted ? "Unmute" : "Mute"} 
              width="24" 
              height="24"
            />
          </button>
        </div>
      );
    }
    return (
      <img
        src={src}
        alt={alt}
        className={className}
        style={style}
        onClick={handleClick}
      />
    );
  };

  const renderModalMedia = () => {
    if (isVideo) {
      return (
        <video
          src={src}
          controls
          className="vi-image-modal-img"
          playsInline
          autoPlay
          muted={isMuted}
        />
      );
    }
    return <img src={src} alt={alt} className="vi-image-modal-img" />;
  };

  return (
    <div className="vi-media-wrapper" style={style}>
      {renderMedia()}
      
      {showModal && createPortal(
        <div className="vi-image-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="vi-image-modal-content" onClick={e => e.stopPropagation()}>
            <button 
              className="vi-image-modal-close" 
              onClick={() => setShowModal(false)}
            >
              <img src={deleteIcon} alt="Close" />
            </button>
            {renderModalMedia()}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default VIImage; 